import { Collapse, Button } from "antd";
import type { CollapseProps } from "antd";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { PipelineGroup, PipelineInfoRow } from "@/components/form/pipelineFieldRow";
import type { Cliente } from "@/features/clientes/types/cliente";
import { formatCpfCnpj, formatPhone, formatDate, formatCurrency } from "@/utils/format";

const PRIORITY_LABELS: Record<string, string> = {
  ALTA: "Alta",
  MEDIA: "Média",
  BAIXA: "Baixa",
  APENAS_PESQUISANDO: "Apenas pesquisando",
};

interface ClienteInfoDisplayProps {
  cliente: Cliente;
  onEdit: () => void;
  compact?: boolean;
  /** Exibe um "X" para desvincular o cliente selecionado */
  onRemove?: () => void;
  telefoneError?: string;
}

function toNumber(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

export function ClienteInfoDisplay({ cliente, onEdit, compact = false, onRemove, telefoneError }: ClienteInfoDisplayProps) {
  const header = (
    <div className="flex items-center justify-between gap-3 px-1">
      <span className={compact ? "text-sm font-semibold text-foreground truncate" : "text-base font-semibold text-foreground truncate"}>
        {cliente.nome}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <Button size={compact ? "small" : "middle"} icon={<EditOutlined />} onClick={onEdit}>
          Editar dados
        </Button>
        {onRemove && (
          <Button
            size={compact ? "small" : "middle"}
            type="text"
            icon={<CloseOutlined />}
            onClick={onRemove}
            aria-label="Remover cliente selecionado"
            title="Remover cliente selecionado"
          />
        )}
      </div>
    </div>
  );

  if (compact) {
    return (
      <div className="space-y-2">
        {header}
        <PipelineGroup>
          <PipelineInfoRow label="CPF/CNPJ" value={formatCpfCnpj(cliente.cpf)} />
          <PipelineInfoRow label="Telefone" value={formatPhone(cliente.tel1)}>
            {telefoneError ? (
              <>
                {formatPhone(cliente.tel1) || "—"}
                <p className="text-[11px] text-red-500 leading-4 mb-1">{telefoneError}</p>
              </>
            ) : undefined}
          </PipelineInfoRow>
          <PipelineInfoRow label="Email" value={cliente.email} />
        </PipelineGroup>
      </div>
    );
  }

  const items: NonNullable<CollapseProps["items"]> = [
    {
      key: "pessoal",
      label: "Informações Pessoais",
      children: (
        <PipelineGroup>
          <PipelineInfoRow label="CPF/CNPJ" value={formatCpfCnpj(cliente.cpf)} />
          <PipelineInfoRow label="Email" value={cliente.email} />
          <PipelineInfoRow label="Telefone Principal" value={formatPhone(cliente.tel1)} />
          <PipelineInfoRow label="Telefone Secundário" value={formatPhone(cliente.tel2)} />
          <PipelineInfoRow label="Data de Nascimento" value={formatDate(cliente.data_nascimento)} />
          <PipelineInfoRow label="RG" value={cliente.rg} />
          <PipelineInfoRow label="Estado Civil" value={cliente.estado_civil?.replace("_", " ")} />
          <PipelineInfoRow label="Naturalidade" value={cliente.naturalidade} />
          <PipelineInfoRow label="Nacionalidade" value={cliente.nacionalidade} />
          <PipelineInfoRow label="Atividade" value={cliente.atividade_desenvolvida} />
          <PipelineInfoRow label="Renda" value={formatCurrency(toNumber(cliente.rendimento_titular))} />
          <PipelineInfoRow label="Nome da Mãe" value={cliente.nome_mae} />
          <PipelineInfoRow label="Nome do Pai" value={cliente.nome_pai} />
        </PipelineGroup>
      ),
    },
    {
      key: "endereco",
      label: "Endereço",
      children: (
        <PipelineGroup>
          <PipelineInfoRow
            label="Logradouro"
            value={cliente.logradouro ? `${cliente.logradouro}, ${cliente.numero || "S/N"}` : undefined}
          />
          <PipelineInfoRow label="Bairro" value={cliente.bairro} />
          <PipelineInfoRow label="CEP" value={cliente.cep} />
          <PipelineInfoRow label="Cidade" value={cliente.cidade} />
          <PipelineInfoRow label="Estado" value={cliente.estado} />
          <PipelineInfoRow label="Complemento" value={cliente.complemento} />
        </PipelineGroup>
      ),
    },
  ];

  if (cliente.banco) {
    items.push({
      key: "banco",
      label: "Dados Bancários",
      children: (
        <PipelineGroup>
          <PipelineInfoRow label="Banco" value={cliente.banco} />
          <PipelineInfoRow label="Agência" value={cliente.agencia} />
          <PipelineInfoRow label="Conta" value={cliente.conta} />
        </PipelineGroup>
      ),
    });
  }

  if (cliente.prioridade || cliente.tipo_bem) {
    items.push({
      key: "qualificacao",
      label: "Qualificação",
      children: (
        <PipelineGroup>
          <PipelineInfoRow
            label="Prioridade"
            value={cliente.prioridade ? PRIORITY_LABELS[cliente.prioridade] || cliente.prioridade : undefined}
          />
          <PipelineInfoRow label="Tipo do Bem" value={cliente.tipo_bem} />
          <PipelineInfoRow label="Valor de Entrada" value={formatCurrency(toNumber(cliente.valor_entrada))} />
        </PipelineGroup>
      ),
    });
  }

  return (
    <div className="space-y-3">
      {header}
      <Collapse
        accordion
        defaultActiveKey={["pessoal"]}
        className="rounded-[16px] overflow-hidden border border-[rgba(15,118,110,0.08)]"
        items={items}
      />
    </div>
  );
}
